import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Copy, Gift, Users, Trophy, Check } from 'lucide-react';

interface ReferralStats {
  referral_code: string | null;
  total_referrals: number;
  pending: number;
  rewarded: number;
  total_rewards: number;
  max_rewards: number;
  rewards: { id: string; reward_type: string; applied: boolean; created_at: string }[];
}

const MAX_REWARDS = 12;

export default function ReferralPage() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [friendCode, setFriendCode] = useState('');
  const [copied, setCopied] = useState(false);

  // Load stats directly from DB — much faster than invoking an edge function
  const loadStats = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [referralsRes, rewardsRes, codeRes] = await Promise.all([
        supabase
          .from('referrals')
          .select('id, status')
          .eq('referrer_id', user.id)
          .not('referred_id', 'is', null),
        supabase
          .from('referral_rewards')
          .select('id, reward_type, applied, created_at')
          .eq('user_id', user.id),
        supabase
          .from('referrals')
          .select('referral_code')
          .eq('referrer_id', user.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);

      const referrals = referralsRes.data || [];
      const rewards = rewardsRes.data || [];

      setStats({
        referral_code: codeRes.data?.referral_code || null,
        total_referrals: referrals.length,
        pending: referrals.filter(r => r.status === 'signed_up').length,
        rewarded: referrals.filter(r => r.status === 'rewarded').length,
        total_rewards: rewards.length,
        max_rewards: MAX_REWARDS,
        rewards,
      });
    } catch (err) {
      console.error('Failed to load referral stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const generateCode = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('process-referral', {
        body: { action: 'generate-code' },
      });
      if (error) throw error;
      if (data?.code) {
        toast.success('Referral code generated!');
        loadStats();
      }
    } catch {
      toast.error('Failed to generate code');
    }
  }, [loadStats]);

  const applyCode = useCallback(async () => {
    if (!friendCode.trim()) { toast.error('Enter a referral code'); return; }
    setApplying(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-referral', {
        body: { action: 'apply-code', referral_code: friendCode.trim() },
      });
      if (error) throw error;
      if (data?.error) { toast.error(data.error); }
      else { toast.success(data.message || 'Referral applied!'); setFriendCode(''); loadStats(); }
    } catch {
      toast.error('Failed to apply code');
    } finally {
      setApplying(false);
    }
  }, [friendCode, loadStats]);

  const checkEligibility = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('process-referral', {
        body: { action: 'check-eligibility' },
      });
      if (error) throw error;
      if (data?.rewards_granted > 0) { toast.success(`${data.rewards_granted} new reward(s) granted!`); loadStats(); }
      else { toast.info('No new rewards yet — referrals need 30 days of active subscription'); }
    } catch {
      toast.error('Failed to check eligibility');
    }
  }, [loadStats]);

  const copyCode = useCallback(() => {
    if (!stats?.referral_code) return;
    navigator.clipboard.writeText(stats.referral_code);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  }, [stats?.referral_code]);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="px-4 pt-6 pb-4">
      <h1 className="mb-1 font-display text-2xl font-bold">Refer a Friend</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Both you and your friend earn 1 free month of Pro after they stay subscribed for 30 days. Max {MAX_REWARDS} rewards.
      </p>

      {/* Your referral code */}
      <Card className="mb-4 border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Your Referral Code</p>
          {loading ? (
            <Skeleton className="h-10 w-full rounded-md" />
          ) : stats?.referral_code ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md bg-background px-3 py-2 text-center font-mono text-lg font-bold tracking-wider">
                {stats.referral_code}
              </code>
              <Button variant="outline" size="icon" onClick={copyCode} title="Copy code">
                {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          ) : (
            <Button onClick={generateCode} className="w-full gap-2">
              <Gift className="h-4 w-4" />
              Generate My Code
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {(['Referred', 'Rewarded', 'Free Months'] as const).map((label, i) => {
          const icons = [<Users className="mx-auto mb-1 h-4 w-4 text-primary" />, <Trophy className="mx-auto mb-1 h-4 w-4 text-accent-foreground" />, <Gift className="mx-auto mb-1 h-4 w-4 text-primary" />];
          const values = [stats?.total_referrals, stats?.rewarded, stats?.total_rewards];
          return (
            <Card key={label}>
              <CardContent className="p-3 text-center">
                {icons[i]}
                {loading ? <Skeleton className="mx-auto mt-1 h-7 w-8" /> : <p className="font-display text-xl font-bold">{values[i] || 0}</p>}
                <p className="text-[10px] text-muted-foreground">{label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Progress bar */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Rewards earned</span>
            <span className="font-medium">{stats?.total_rewards || 0} / {MAX_REWARDS}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${Math.min(((stats?.total_rewards || 0) / MAX_REWARDS) * 100, 100)}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Check eligibility */}
      {!loading && (stats?.pending || 0) > 0 && (
        <Button variant="outline" className="mb-4 w-full gap-2" onClick={checkEligibility}>
          <Trophy className="h-4 w-4" />
          Check for New Rewards ({stats?.pending} pending)
        </Button>
      )}

      {/* Apply a friend's code */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Have a referral code?</p>
          <div className="flex gap-2">
            <Input
              placeholder="Enter code e.g. AB-1234-XYZ"
              value={friendCode}
              onChange={e => setFriendCode(e.target.value)}
              className="font-mono uppercase"
            />
            <Button onClick={applyCode} disabled={applying}>
              {applying ? 'Applying...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reward history */}
      {!loading && stats?.rewards && stats.rewards.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Reward History</p>
          <div className="space-y-2">
            {stats.rewards.map(reward => (
              <Card key={reward.id}>
                <CardContent className="flex items-center gap-3 p-3">
                  <Gift className="h-4 w-4 shrink-0 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">1 Free Month of Pro</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(reward.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <Badge variant={reward.applied ? 'secondary' : 'default'} className="text-[10px]">
                    {reward.applied ? 'Applied' : 'Available'}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <Card className="mt-4">
        <CardContent className="p-4">
          <p className="mb-2 text-xs font-semibold">How it works</p>
          <ol className="space-y-1.5 text-xs text-muted-foreground list-decimal pl-4">
            <li>Share your unique referral code with friends</li>
            <li>They sign up and apply your code</li>
            <li>Once they maintain a Pro subscription for 30 days, you both earn 1 free month</li>
            <li>Earn up to 12 free months (1 year) of Pro!</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
